import { useCopilotChat } from '@copilotkit/react-core'
import { type InputProps } from '@copilotkit/react-ui'
import '@copilotkit/react-ui/styles.css'
import { AgentSelect } from '@egw/ai-chat'
import { type DefaultProps } from '@egw/components'
import { WEALTH_ROUTES, type FeatureFlags } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { ArrowUpIcon, StopIcon } from '@heroicons/react/16/solid'
import cn from 'classnames'
import { useEffect, useRef, useState, type FC, type KeyboardEvent, type MutableRefObject } from 'react'
import { useLocation } from 'react-router'
import { twMerge } from 'tailwind-merge'
import { ChatVariant } from '../../types/chat'
import { ChatLogo } from '../chat-logo/chat-logo'

interface ChatInputProps extends InputProps, DefaultProps {
  onOpenChat: () => void
  variant?: ChatVariant
  isChatOpen?: boolean
  isEventSentRef: MutableRefObject<boolean>
  setError: (error: string | Error | null) => void
}

const MAX_MESSAGE_LENGTH = 2000 // Character / Token Limit: 2000 / 413 Tokens

export const ChatInput: FC<ChatInputProps> = ({
  inProgress,
  onSend,
  onOpenChat,
  className,
  testId,
  id,
  ariaLabel,
  variant = 'default',
  isChatOpen = false,
  isEventSentRef,
  setError,
}) => {
  const { t } = useTranslation('features-chat')
  const { stopGeneration, isLoading } = useCopilotChat()
  const { pathname } = useLocation()
  const { flags } = useFlags<FeatureFlags>()

  const [inputValue, setInputValue] = useState('')
  // TODO: remove this after testing
  const [selectedAgent, setSelectedAgent] = useState<string>(() => {
    return localStorage.getItem('selected-agent') || 'advice'
  })
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isIntelligencePage = pathname === WEALTH_ROUTES.EVERGREEN_INTELLIGENCE.path

  const handleAgentChange = (agentId: string): void => {
    setSelectedAgent(agentId)
    localStorage.setItem('selected-agent', agentId)
  }

  // Auto-focus textarea when chat opens
  useEffect(() => {
    if (textareaRef.current && isChatOpen && !isIntelligencePage) {
      textareaRef.current.focus()
    }
  }, [isChatOpen])

  const handleSend = async (): Promise<void> => {
    setError(null)
    if (inputValue.trim()) {
      await onSend(inputValue)
      setInputValue('')
      // Reset textarea height after sending
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = async (e: KeyboardEvent<HTMLTextAreaElement>): Promise<void> => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      await handleSend()
    }
  }

  const renderActionButton = (): JSX.Element | null => {
    if (variant !== ChatVariant.EXPANDED) return null

    if (inProgress) {
      return (
        <button
          type="button"
          data-testid="chat-input-stop-button"
          onClick={() => {
            stopGeneration()
          }}
          className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors bg-black hover:bg-gray-700 cursor-pointer"
        >
          <StopIcon className="size-6 text-white" />
        </button>
      )
    }

    return (
      <button
        type="button"
        disabled={!inputValue.trim()}
        data-testid="chat-input-send-button"
        onClick={() => {
          void handleSend()
        }}
        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
          !inputValue.trim() ? 'bg-gray-300 cursor-not-allowed' : 'bg-black hover:bg-gray-700 cursor-pointer'
        }`}
      >
        <ArrowUpIcon className="size-6 text-white" />
      </button>
    )
  }

  const finalClasses = twMerge(
    `flex flex-col bg-[#F8F7F5] rounded-t-3xl shadow-[0px_0px_8px_-1px_rgba(182,185,183,1)] border border-gray-200 px-lg pt-sm pb-lg md:max-w-[816px] mx-auto w-full ${
      variant === ChatVariant.EXPANDED ? 'gap-sm' : ''
    }`,
    className,
  )

  return (
    <div
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      className={finalClasses}
    >
      <div className="relative flex items-start">
        <ChatLogo containerClassName={cn('mt-1', { 'opacity-40': isLoading })} />

        {/* Input field */}
        <textarea
          ref={textareaRef}
          disabled={inProgress}
          value={inputValue}
          data-testid="chat-input-textarea"
          onChange={(e) => {
            setInputValue(e.target.value)
          }}
          onKeyDown={(e) => {
            void handleKeyDown(e)
          }}
          onClick={() => {
            if (!isEventSentRef.current) {
              sendEvent('user_evergreenIntelligence_formFocus', {
                eventSource: 'egWealth',
                category: 'evergreen-intelligence',
                subCategory: 'result',
              })
              isEventSentRef.current = true
            }

            if (variant === 'default') {
              onOpenChat()
            } else {
              return undefined
            }
          }}
          placeholder={t('chat.placeholder')}
          className={cn(
            'flex-1 mt-1.5 bg-transparent border-none outline-none text-text-header typo-primary-base-light ml-xs resize-none overflow-y-auto min-h-6 max-h-32',
            {
              'placeholder-text-gray-400': isLoading,
              'placeholder-text-header': !isLoading,
            },
          )}
          rows={1}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement
            target.style.height = 'auto'
            target.style.height = `${Math.min(target.scrollHeight, 124).toString()}px`
          }}
          maxLength={MAX_MESSAGE_LENGTH}
        />
      </div>
      <div className="flex items-center justify-end">
        {/* TODO: Add microphone icon back in later */}
        {/* Microphone icon */}
        {/* <button
          type="button"
          disabled={inProgress || !inputValue.trim()}
          onClick={() => {
            void handleSend()
          }}
          className={cx('size-8 flex items-center justify-center', {
            'cursor-pointer': !inProgress && inputValue.trim(),
          })}
        >
          <MicrophoneIcon className="size-6 text-black cursor-pointer hover:text-gray-700" />
        </button> */}

        <div className="flex items-center gap-2xs">
          {/* Agent Selection */}
          {variant === ChatVariant.EXPANDED ? (
            flags.rolloutEvergreenIntelligenceUiAgentSelector ? (
              <AgentSelect selectedAgent={selectedAgent} onAgentChange={handleAgentChange} />
            ) : null
          ) : null}
          {renderActionButton()}
        </div>
      </div>
    </div>
  )
}
